/**
 * An online match as both sides build it (docs/architecture.md §7): the server and each client
 * create the same fight and the same bots from this, so their simulations stay identical.
 */
import type { BotDifficulty } from '../config/bot';
import type { Look } from '../config/cosmetics';
import type { SkillRanks } from '../config/skills';
import { createBot, type BotState, type GamePlan } from '../bot/bot';
import { arenaById } from '../sim/arena';
import { createFight } from '../sim/fight';
import { fighterSetup } from '../sim/mods';
import type { FightState, Side } from '../sim/types';

export interface OnlineFighter {
  name: string;
  fighterId: string;
  ranks: SkillRanks;
  level: number;
  /** Two-letter country code, when known. */
  country?: string;
  /** The Locker look (M7), from the server's progress: looks only, never in the simulation. */
  look?: Look;
  /** Set when a bot plays this side (matchmaking bot fill). */
  bot?: { difficulty: BotDifficulty; plan?: GamePlan | null; seed: number };
}

export interface OnlineMatch {
  seed: number;
  arenaId: string;
  fighters: readonly [OnlineFighter, OnlineFighter];
}

export function createMatchFight(m: OnlineMatch): FightState {
  return createFight({
    arena: arenaById(m.arenaId),
    fighters: [fighterSetup(m.fighters[0].ranks), fighterSetup(m.fighters[1].ranks)],
    seed: m.seed,
  });
}

/** The bot for a side, if a bot plays it (fresh: call once per timeline). */
export function createMatchBot(m: OnlineMatch, side: Side): BotState | null {
  const b = m.fighters[side].bot;
  return b ? createBot({ side, difficulty: b.difficulty, seed: b.seed, plan: b.plan ?? null }) : null;
}

/** Deep copy of a bot's state (rollback keeps one per confirmed tick). */
export function cloneBot(b: BotState): BotState {
  return { ...b, seen: b.seen.map((o) => ({ ...o })) };
}
